import {
  CART_SELECTION_SNAPSHOT_VERSION,
  CART_SELECTION_TTL_MS,
  type CartSelectionScope,
  type CartSelectionSnapshotV1,
  type RestoredCartSelection,
} from '@/domain/cart/selection/types';

export {
  CART_SELECTION_SNAPSHOT_VERSION,
  CART_SELECTION_TTL_MS,
} from '@/domain/cart/selection/types';
export type {
  CartSelectionScope,
  CartSelectionSnapshotV1,
  RestoredCartSelection,
} from '@/domain/cart/selection/types';

type CreateCartSelectionSnapshotInput = {
  now: number;
  scope: CartSelectionScope;
  selectedCartIds: number[];
  explicitlyEmpty: boolean;
};

type ParseCartSelectionSnapshotOptions = {
  now: number;
  scope: CartSelectionScope;
};

/** 与历史 V2 key 保持一致，供恢复及游客登录迁移共用。 */
export const getCartSelectionStorageKey = (
  { site, currency, identity }: CartSelectionScope,
  namespace: 'live' | 'preview' = 'live',
) =>
  `zl-cart-v1-selection:v${CART_SELECTION_SNAPSHOT_VERSION}:${namespace}:${encodeURIComponent(site)}:${encodeURIComponent(currency)}:${encodeURIComponent(identity ?? '')}`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isFiniteTimestamp = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;

const normalizeCartIds = (value: unknown): number[] | null => {
  if (!Array.isArray(value)) {
    return null;
  }

  const cartIds = new Set<number>();
  for (const cartId of value) {
    if (typeof cartId !== 'number' || !Number.isSafeInteger(cartId) || cartId <= 0) {
      return null;
    }
    cartIds.add(cartId);
  }

  return Array.from(cartIds);
};

export const createCartSelectionSnapshot = ({
  now,
  scope,
  selectedCartIds,
  explicitlyEmpty,
}: CreateCartSelectionSnapshotInput): CartSelectionSnapshotV1 => ({
  version: CART_SELECTION_SNAPSHOT_VERSION,
  scope,
  selectedCartIds: Array.from(new Set(selectedCartIds)),
  explicitlyEmpty,
  updatedAt: now,
  expiresAt: now + CART_SELECTION_TTL_MS,
});

export const parseCartSelectionSnapshot = (
  raw: string | null,
  { now, scope }: ParseCartSelectionSnapshotOptions,
): RestoredCartSelection | null => {
  if (!raw) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!isRecord(parsed) || parsed.version !== CART_SELECTION_SNAPSHOT_VERSION) {
    return null;
  }

  if (
    !isRecord(parsed.scope) ||
    parsed.scope.site !== scope.site ||
    parsed.scope.currency !== scope.currency ||
    !scope.identity ||
    parsed.scope.identity !== scope.identity
  ) {
    return null;
  }

  const selectedCartIds = normalizeCartIds(parsed.selectedCartIds);
  if (selectedCartIds === null || typeof parsed.explicitlyEmpty !== 'boolean') {
    return null;
  }

  if ((selectedCartIds.length === 0) !== parsed.explicitlyEmpty) {
    return null;
  }

  if (
    !isFiniteTimestamp(parsed.updatedAt) ||
    !isFiniteTimestamp(parsed.expiresAt) ||
    parsed.expiresAt !== parsed.updatedAt + CART_SELECTION_TTL_MS ||
    now >= parsed.expiresAt
  ) {
    return null;
  }

  return { selectedCartIds, explicitlyEmpty: parsed.explicitlyEmpty };
};
